import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import UserAvatar from "./UserAvatar";

export default function HamburgerNav() {
  const [open, setOpen] = useState(false);
  const { t, lang, setLang } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();

  const links = [
    { path: "/", key: "nav.home" },
    { path: "/kalkulators", key: "nav.calculator" },
    { path: "/kalendars", key: "nav.calendar" },
    { path: "/projekti", key: "nav.myProjects" },
    { path: "/galerija", key: "nav.gallery" },
    { path: "/kontakti", key: "nav.contact" },
    { path: "/faq", key: "nav.faq" },
  ];

  const close = () => setOpen(false);

  return (
    <>
      {/* Fixed Logo */}
      <Link
        to="/"
        onClick={close}
        data-testid="nav-logo"
        className="fixed top-4 left-4 z-[100] no-underline"
        style={{ textDecoration: "none" }}
      >
        <div
          className="bg-[#FF6600] px-3 py-2 border-2 border-black"
          style={{ boxShadow: "3px 3px 0 #000" }}
        >
          <span
            className="font-black text-xl tracking-tighter text-black"
            style={{ fontFamily: "Chivo, sans-serif", letterSpacing: "-0.04em" }}
          >
            RMD
          </span>
        </div>
      </Link>

      {/* Hamburger Button + User Avatar */}
      <div style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 100, display: 'flex', gap: '10px', alignItems: 'center' }}>
        <UserAvatar />
        <button
          data-testid="hamburger-btn"
          onClick={() => setOpen((v) => !v)}
          className="bg-black text-white p-3 border-2 border-black hover:bg-[#FF6600] hover:text-black transition-colors duration-150"
          aria-label="Toggle navigation"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Full-screen overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[90] bg-black flex flex-col justify-center px-8 md:px-16"
          >
            <nav className="space-y-1 mb-10">
              {links.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ x: -40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.06, ease: "easeOut" }}
                >
                  <Link
                    to={link.path}
                    onClick={close}
                    data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                    style={{ fontFamily: "Chivo, sans-serif" }}
                    className={`block text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight leading-none py-1 transition-colors duration-100 no-underline ${
                      location.pathname === link.path
                        ? "text-[#FF6600]"
                        : "text-white hover:text-[#FF6600]"
                    }`}
                  >
                    {t(link.key)}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <div className="flex flex-wrap items-center gap-4">
              {/* Auth buttons */}
              {user ? (
                <div className="flex items-center gap-4">
                  <span className="text-white/60 text-sm">{user.name}</span>
                  <button
                    data-testid="nav-logout-btn"
                    onClick={() => { logout(); close(); }}
                    className="rmd-btn-secondary text-sm"
                  >
                    {t("nav.logout")}
                  </button>
                </div>
              ) : null}

              {/* Language switcher */}
              <div className="ml-auto flex gap-2">
                {["lv", "ru", "en"].map((l) => (
                  <button
                    key={l}
                    data-testid={`lang-btn-${l}`}
                    onClick={() => setLang(l)}
                    className={`px-3 py-1 text-xs font-bold uppercase tracking-widest border-2 transition-all ${
                      lang === l
                        ? "bg-[#FF6600] text-black border-[#FF6600]"
                        : "text-white border-white/30 hover:border-white hover:text-white"
                    }`}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 border-t border-white/10 pt-6">
              <p className="text-white/30 text-xs">
                info@realmendo.com &nbsp;·&nbsp; +371 25878923 &nbsp;·&nbsp; Cēsis, Latvija
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
